import React, { useState } from 'react'
import {
  Button, Fieldset, Stack, NativeSelect, CloseButton, Dialog,
  Field, Input, Portal, FileUpload, InputGroup
} from '@chakra-ui/react'
import { LuFileUp } from 'react-icons/lu'
import { toaster } from '@/components/ui/toaster';
import { useTeams } from '@/hooks/useTeams';

export function HandleCreateTeam() {
  const { createTeam } = useTeams();
  const [logoFile, setLogoFile] = useState<File | null>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const formData = new FormData(form)

    const name = formData.get('name') as string
    const description = formData.get('description') as string
    const visibility = formData.get('visibility') as 'private' | 'public'

    try {
      await createTeam({
        name,
        description,
        visibility,
        logo: logoFile,
      })

    } catch (err) {
      toaster.create({
        title: "Failed to create team",
        type: "error",
        description: 'Failed to create team',
        duration: 3000,
        closable: true,
      })
    }
  }

  return (
    <Dialog.Root role="alertdialog">
      <Dialog.Trigger asChild>
        <Button variant="outline" size="sm">Create Team</Button>
      </Dialog.Trigger>

      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content as="form" onSubmit={handleSubmit}>
            <Dialog.Header>
              <Dialog.Title>Create new team</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <Fieldset.Root size="lg" maxW="md">
                <Stack>
                  <Fieldset.Legend>Team Info</Fieldset.Legend>
                  <Fieldset.HelperText>Create a shared space for your content team.</Fieldset.HelperText>
                </Stack>

                <Fieldset.Content>
                  <Field.Root>
                    <Field.Label>Name</Field.Label>
                    <Input name="name" required />
                    <Field.HelperText>e.g. VibeMakers</Field.HelperText>
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Description</Field.Label>
                    <Input name="description" />
                    <Field.HelperText>Short description...</Field.HelperText>
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Visibility</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field name="visibility">
                        <option value="private">Private</option>
                        <option value="public" disabled title="Coming soon">Public</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>

                  <FileUpload.Root
                    gap="1"
                    maxWidth="300px"
                    maxFiles={1}
                    accept="image/*"
                    onFileChange={(files) => setLogoFile(files.acceptedFiles[0] ?? null)}
                  >
                    <FileUpload.HiddenInput />
                    <FileUpload.Label>Team Logo (optional)</FileUpload.Label>
                    <InputGroup
                      startElement={<LuFileUp />}
                      endElement={
                        <FileUpload.ClearTrigger asChild>
                          <CloseButton
                            me="-1"
                            size="xs"
                            variant="plain"
                            focusVisibleRing="inside"
                            focusRingWidth="2px"
                            pointerEvents="auto"
                          />
                        </FileUpload.ClearTrigger>
                      }
                    >
                      <Input asChild>
                        <FileUpload.Trigger>
                          <FileUpload.FileText lineClamp={1} />
                        </FileUpload.Trigger>
                      </Input>
                    </InputGroup>
                  </FileUpload.Root>
                </Fieldset.Content>
              </Fieldset.Root>
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button colorPalette="red" alignSelf="flex-start" mr={5}>Cancel</Button>
              </Dialog.ActionTrigger>
              <Button type="submit" alignSelf="flex-end">Create</Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
